//core
import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { isValid } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimeRangePicker, DateRange, SingleInputDateTimeRangeField } from "@mui/x-date-pickers-pro";
import { Box } from "@mui/material";

//styles
import { useStyles } from "./styles";

//hooks
import { useLocale } from "../../../../customHooks/useLocale";

interface IMuiDateTimeRangePickerProps {
  nameFrom: string;
  nameTo: string;
  label?: string;
  disabled?: boolean;
  isRequired?: boolean;
  minDate?: any;
  maxDate?: any;
  minDateTime?: any;
  maxDateTime?: any;
}

const MuiDateTimeRangePicker: React.FC<IMuiDateTimeRangePickerProps> = ({
  nameFrom,
  nameTo,
  label = '',
  disabled = false,
  isRequired = false,
  minDate,
  maxDate,
  minDateTime,
  maxDateTime,
}): JSX.Element => {
  const styles = useStyles();
  const locale = useLocale();

  const { values, touched, errors, setFieldValue, setFieldError, setFieldTouched } = useFormikContext<any>();

  const formikStart = values[nameFrom];
  const formikEnd = values[nameTo];

  const [value, setValue] = useState<DateRange<Date>>(() => {
    const safeStart = !!formikStart && isValid(new Date(formikStart)) ? new Date(formikStart) : null;
    const safeEnd = !!formikEnd && isValid(new Date(formikEnd)) ? new Date(formikEnd) : null;
    return [safeStart, safeEnd];
  });

  useEffect(() => {
    const safeStart = !!formikStart && isValid(new Date(formikStart)) ? new Date(formikStart) : null;
    const safeEnd = !!formikEnd && isValid(new Date(formikEnd)) ? new Date(formikEnd) : null;

    setValue([safeStart, safeEnd]);
  }, [formikStart, formikEnd]);

  const isBeforeMin = (date: Date): boolean => !!minDateTime && date < new Date(minDateTime);
  const isAfterMax = (date: Date): boolean => !!maxDateTime && date > new Date(maxDateTime);
  const isOutOfRange = (date: Date | null): boolean => !!date && (isBeforeMin(date) || isAfterMax(date));

  const validateRange = (start: Date | null, end: Date | null): string | undefined => {
    if (isRequired && (!start || !end)) {
      return 'Required field';
    }
    if ((!!start && !isValid(start)) || (!!end && !isValid(end))) {
      return 'Invalid date provided';
    }
    if (!!start && !!end && start > end) {
      return 'Start date cannot be after end date';
    }
    if (isOutOfRange(start) || isOutOfRange(end)) {
      return 'Date is out of the allowed range';
    }

    return undefined;
  };

  useEffect(() => {
    const validationError = validateRange(formikStart ? new Date(formikStart) : null, formikEnd ? new Date(formikEnd) : null);
    setFieldError(nameFrom, validationError);
    setFieldError(nameTo, validationError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (newValue: DateRange<Date>) => {
    const [start, end] = newValue;
    setValue(newValue);

    const validationError = validateRange(start, end);
    setFieldError(nameFrom, validationError);
    setFieldError(nameTo, validationError);

    setFieldValue(nameFrom, start, false);
    setFieldValue(nameTo, end, false);
  };

  const clampDate = (date: Date | null): Date | null => {
    if (!date || !isValid(date)) {
      return date;
    }
    if (isBeforeMin(date)) {
      return new Date(minDateTime);
    }
    if (isAfterMax(date)) {
      return new Date(maxDateTime);
    }

    return date;
  };

  const handleAccept = (newValue: DateRange<Date>) => {
    const [start, end] = newValue;
    handleChange([clampDate(start), clampDate(end)]);
  };

  const handleClose = () => {
    setFieldTouched(nameFrom, true, true);
    setFieldTouched(nameTo, true, true);
  };

  const hasError = !!(touched[nameFrom] || touched[nameTo]) && !!(errors[nameFrom] || errors[nameTo]);

  return (
    <Box className={styles.field}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale.locale}>
        <DateTimeRangePicker
          label={`${label}${isRequired ? '*' : ''}`}
          value={value}
          onChange={handleChange}
          onAccept={handleAccept}
          onClose={handleClose}
          ampm={false}
          disabled={disabled}
          format="dd/MM/yyyy HH:mm"
          {...(maxDate ? { maxDate: new Date(maxDate) } : {})}
          {...(minDate ? { minDate: new Date(minDate) } : {})}
          {...(maxDateTime ? { maxDateTime: new Date(maxDateTime) } : {})}
          {...(minDateTime ? { minDateTime: new Date(minDateTime) } : {})}
          slots={{ field: SingleInputDateTimeRangeField }}
          slotProps={{
            textField: {
              size: 'small',
              error: hasError,
              helperText: hasError ? (errors[nameFrom] || errors[nameTo]) as string : '',
            },
            actionBar: {
              actions: ['accept'],
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default MuiDateTimeRangePicker;
